import React, { useState, useEffect } from "react";
import { Typography, CircularProgress, Box, Alert, Grid } from "@mui/material";

// Assumindo que CompetitionCard é um default export e Competition é um named export/type
import CompetitionCard, { Competition } from "../CompetitionCard";

interface ListarCompeticaoProps {
  token: string;
  refetchTrigger: number;
}

interface ApiResponse {
  data: Competition[];
  total: number;
}

const API_URL = "https://api.anuncio360.com.br/admin/scale-competitions";

const ListarCompeticao: React.FC<ListarCompeticaoProps> = ({
  token,
  refetchTrigger,
}) => {
  const [competitions, setCompetitions] = useState<Competition[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Função para formatar o preço (mantida)
  const formatCurrency = (price: string) => {
    try {
      const value = parseFloat(price);
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(value);
    } catch (e) {
      return `R$ ${price?.replace(".", ",") || "0,00"}`;
    }
  };

  // Função de Busca de Dados (Mantida)
  const fetchCompetitions = async () => {
    if (!token) {
      // ...
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(API_URL, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        const errorData = errorText ? JSON.parse(errorText) : {};
        throw new Error(errorData.message || `Erro HTTP: ${response.status}`);
      }

      const result: ApiResponse = await response.json();
      setCompetitions(result.data);
    } catch (err: any) {
      console.error("Erro ao buscar competições:", err);
      setError(err.message || "Erro desconhecido ao carregar as competições.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    console.log(`useEffect disparado. Trigger: ${refetchTrigger}`);
    fetchCompetitions();
  }, [token, refetchTrigger]);

  const handleUpdateCompetitionData = (updatedCompetition: Competition) => {
    setCompetitions((prevCompetitions) =>
      prevCompetitions.map((comp) =>
        comp.id === updatedCompetition.id ? updatedCompetition : comp
      )
    );
  };

  const handleDeleteCompetitionData = (deletedCompetitionId: string) => {
    setCompetitions((prevCompetitions) =>
      prevCompetitions.filter((comp) => comp.id !== deletedCompetitionId)
    );
  };

  // Renderização Condicional: Loading, Erro e Lista Vazia (mantidas)
  if (isLoading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" p={3}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Carregando competições...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3} sx={{ maxWidth: 600, margin: "auto" }}>
        <Alert severity="error">
          <Typography variant="h6">Erro de Carregamento</Typography>
          {error}
        </Alert>
      </Box>
    );
  }

  if (competitions.length === 0) {
    return (
      <Box p={3} sx={{ maxWidth: 600, margin: "auto" }}>
        <Alert severity="info">Nenhuma competição encontrada.</Alert>
      </Box>
    );
  }

  // Renderização em Cards com Grid (Passando os handlers)
  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        align="center"
        sx={{ mb: 4 }}
      >
        Minhas Competições
      </Typography>

      <Grid container spacing={4} justifyContent="center">
        {competitions.map((competition) => (
          <CompetitionCard
            token={token}
            key={competition.id}
            competition={competition}
            formatCurrency={formatCurrency}
            onCompetitionDataUpdated={handleUpdateCompetitionData}
            onCompetitionDeleted={handleDeleteCompetitionData}
          />
        ))}
      </Grid>
    </Box>
  );
};

export type { Competition };

export default ListarCompeticao;
